// EarningsEngine.js
// Centralized, safe earnings calculation for Simple mode
// Ensures NaN never silently becomes $0

import { calculateDistance } from "../data/airports.js";
import { getYieldMultiplier } from "./RouteDemandSystem.js";
import { debug } from '../lib/debug.js';

/**
 * Calculate earnings safely with validation
 * Returns { revenue, costs, profit, distance } or { ..., _invalid: true } on error
 * @param {Object} aircraft - Aircraft object with capacity
 * @param {string} origin - Origin airport code
 * @param {string} destination - Destination airport code
 * @param {Object} demandState - Optional routeDemand state for yield multiplier
 */
export function calculateEarningsSafe(aircraft, origin, destination, demandState = null) {
  const distance = calculateDistance(origin, destination);

  if (!Number.isFinite(distance) || distance <= 0) {
    console.warn("[calculateEarningsSafe] invalid distance", { origin, destination, distance });
    return { revenue: 0, costs: 0, profit: 0, distance: 0, _invalid: true };
  }

  const capacity = Number(aircraft?.capacity ?? 76);
  const pax = Math.floor(capacity * 0.85);
  const ticketPrice = 10 * distance; // $10 per nm per seat
  let revenue = Math.round(pax * ticketPrice);

  // Apply route demand multiplier if demandState provided
  // High demand (100%) = full revenue, saturated (30%) = 30% revenue
  if (demandState) {
    const demandMultiplier = getYieldMultiplier(origin, destination, demandState);
    revenue = Math.round(revenue * demandMultiplier);
  }

  const costs = Math.round(distance * 1 + 500); // $1/nm + $500 base
  const profit = revenue - costs;

  return { revenue, costs, profit, distance, passengers: pax, loadFactor: 0.85 };
}

/**
 * Get flight earnings with fallback recomputation
 * Use existing preCalculatedEarnings if valid, otherwise recompute
 */
export function getFlightEarnings(flight, aircraft) {
  const pe = flight?.preCalculatedEarnings;

  // Use preCalculatedEarnings if it looks valid
  if (pe && Number.isFinite(pe.revenue) && pe.revenue > 0) {
    return pe;
  }

  // Recompute from route
  const result = calculateEarningsSafe(aircraft, flight?.origin, flight?.destination);

  if (result._invalid) {
    console.warn("[getFlightEarnings] Could not compute valid earnings", {
      flightId: flight?.id,
      origin: flight?.origin,
      destination: flight?.destination,
      preCalc: pe
    });
  }

  return result;
}

/**
 * Apply active buffs to earnings (e.g., Mythic double revenue)
 * Call this when finalizing earnings before adding to cash
 * @param {Object} earnings - Earnings object with revenue, profit
 * @param {Object} buffs - worldState.buffs object
 * @returns {Object} Modified earnings with buffs applied
 */
export function applyEarningsBuffs(earnings, buffs) {
  if (!earnings || !buffs) return earnings;

  let { revenue, profit, costs, ...rest } = earnings;

  // MYTHIC BUFF: Double revenue (check real time expiration)
  const doubleRevenueBuff = buffs?.doubleRevenue;
  if (doubleRevenueBuff?.active && doubleRevenueBuff?.expiresAt && Date.now() < doubleRevenueBuff.expiresAt) {
    const originalRevenue = revenue;
    revenue = Math.round(revenue * 2);
    profit = revenue - (costs || 0);
    debug(`[EarningsEngine] MYTHIC BUFF: Double revenue! $${originalRevenue.toLocaleString()} → $${revenue.toLocaleString()}`);
  }

  return { revenue, profit, costs, ...rest, _buffsApplied: true };
}

/**
 * Check if double revenue buff is currently active
 * @param {Object} buffs - worldState.buffs object
 * @returns {boolean} True if buff is active and not expired
 */
export function isDoubleRevenueActive(buffs) {
  const buff = buffs?.doubleRevenue;
  return buff?.active && buff?.expiresAt && Date.now() < buff.expiresAt;
}

export default { calculateEarningsSafe, getFlightEarnings, applyEarningsBuffs, isDoubleRevenueActive };
